const DEFAULT_LABEL_FONT_SIZE = 14;
const DEFAULT_LABEL_LINE_HEIGHT = 1.3;
const DEFAULT_LABEL_GAP = 8;

function measureFacetLabelHeight(fontSize = DEFAULT_LABEL_FONT_SIZE) {
  return Math.ceil(fontSize * DEFAULT_LABEL_LINE_HEIGHT);
}

/**
 * @param {CanvasRenderingContext2D} context
 * @param {string} text
 * @param {number} maxWidth
 * @param {number} fontSize
 */
function fitFacetLabelText(context, text, maxWidth, fontSize) {
  let label = String(text || "").toUpperCase();
  context.font = `700 ${fontSize}px Arial, Helvetica, sans-serif`;

  if (context.measureText(label).width <= maxWidth) {
    return label;
  }

  while (label.length > 1 && context.measureText(`${label}…`).width > maxWidth) {
    label = label.slice(0, -1);
  }

  return `${label}…`;
}

/**
 * Stitch facet mini-chart canvases into one PNG for print capture.
 * @param {HTMLCanvasElement[]} canvases
 * @param {{ columns?: number, gap?: number, background?: string, labels?: string[], labelFontSize?: number, labelGap?: number, labelColor?: string }} [options]
 * @returns {string}
 */
export function stitchChartCanvasesToDataUrl(canvases, options = {}) {
  const sources = (canvases || []).filter(Boolean);
  if (!sources.length) return "";

  const columns = Math.max(1, options.columns ?? 3);
  const gap = options.gap ?? 12;
  const background = options.background ?? "#ffffff";
  const labels = options.labels || [];
  const hasLabels = labels.some((label) => String(label || "").trim());
  const labelFontSize = options.labelFontSize ?? DEFAULT_LABEL_FONT_SIZE;
  const labelHeight = hasLabels ? measureFacetLabelHeight(labelFontSize) : 0;
  const labelGap = hasLabels ? (options.labelGap ?? DEFAULT_LABEL_GAP) : 0;
  const labelColor = options.labelColor ?? "#111111";
  const rows = Math.ceil(sources.length / columns);
  const cellWidth = Math.max(...sources.map((canvas) => canvas.width));
  const chartHeight = Math.max(...sources.map((canvas) => canvas.height));
  const cellHeight = chartHeight + labelHeight + labelGap;
  const totalWidth = columns * cellWidth + (columns - 1) * gap;
  const totalHeight = rows * cellHeight + (rows - 1) * gap;

  const output = document.createElement("canvas");
  output.width = totalWidth;
  output.height = totalHeight;
  const context = output.getContext("2d");
  if (!context) return "";

  context.fillStyle = background;
  context.fillRect(0, 0, totalWidth, totalHeight);

  sources.forEach((source, index) => {
    const column = index % columns;
    const row = Math.floor(index / columns);
    const x = column * (cellWidth + gap);
    const y = row * (cellHeight + gap);
    const label = labels[index];

    if (hasLabels && label) {
      const labelText = fitFacetLabelText(context, label, cellWidth - 8, labelFontSize);
      context.fillStyle = labelColor;
      context.font = `700 ${labelFontSize}px Arial, Helvetica, sans-serif`;
      context.textAlign = "center";
      context.textBaseline = "top";
      context.fillText(labelText, x + cellWidth / 2, y);
    }

    const chartY = y + labelHeight + labelGap;
    context.drawImage(source, x, chartY, cellWidth, chartHeight);
  });

  return output.toDataURL("image/png", 1);
}

export { fitFacetLabelText, measureFacetLabelHeight };
